import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { Subject, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  map,
  tap,
} from 'rxjs/operators';
import { BackendApiService } from '../../../../../services/backend-api.service';
import { UserService } from '../../../../../services/core/user.service';
import { StaffService } from '../../../../../services/staff/staff.service';
import { taskAnimations } from '../task-animations';
import { resolveProfileImageUrl } from 'src/app/utils/utils.function';

/**
 * Interface for sidebar field change events
 */
export interface TaskSidebarFieldChange {
  field: string;
  value: any;
  validation?: any;
}

/**
 * Interface for project information displayed in sidebar header
 */
export interface TaskProjectInfo {
  moduleId?: number;
  moduleName?: string;
  referenceId?: number;
  referenceName?: string;
}

/**
 * Interface for user/person objects
 */
export interface TaskPerson {
  id?: number;
  name: string;
  full_name?: string;
  email?: string;
  avatar?: string;
  profile_url?: string;
  profile_image?: string;
}

/**
 * Interface for milestone objects
 */
export interface TaskMilestone {
  id: number;
  name: string;
  dueDate: string | Date;
  status: 'upcoming' | 'in-progress' | 'completed' | 'overdue';
  startDate?: string | Date;
  endDate?: string | Date;
  progress?: number;
  taskStats?: {
    total: number;
    completed: number;
  };
  daysRemaining?: number;
  description?: string;
  assignedTo?: string;
}

/**
 * Interface for tag objects
 */
export interface TaskTag {
  id?: number;
  name: string;
  color: string;
}

/**
 * TaskSidebarComponent
 *
 * Dark-themed sidebar panel for task details and metadata.
 * Features collapsible layout, milestone selector, people management, and tags.
 * Matches prototype design with pixel-perfect accuracy.
 *
 * @selector app-task-sidebar
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-task-sidebar',
    templateUrl: './task-sidebar.component.html',
    styleUrls: ['./task-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: taskAnimations,
    standalone: false
})
export class TaskSidebarComponent implements OnInit, OnChanges {
  // Exposed for template use (ng-select item templates bind [profileUrl] directly)
  public readonly resolveProfileImageUrl = resolveProfileImageUrl;

  // =============================================================================
  // INPUTS
  // =============================================================================

  /**
   * Task form group containing all form controls
   */
  @Input() taskForm!: FormGroup;

  /**
   * Project name displayed in header
   */
  @Input() projectName: string = '';

  /**
   * Task ID displayed in header
   */
  @Input() taskId: string | number = '';

  /**
   * List of available milestones for selection
   */
  @Input() milestones: TaskMilestone[] = [];

  /**
   * Currently selected milestone
   */
  @Input() selectedMilestone: TaskMilestone | null = null;

  /**
   * The logged-in user's saved default person per field (GET
   * tasks/default-people, fetched once by the parent, create mode only),
   * keyed by 'assignee'/'observer'/'participant'. Drives which dropdown row
   * gets starred instead of just whichever the API returns first.
   */
  @Input() savedDefaults: any = {};

  /**
   * Task assignees (multiple users)
   */
  @Input() assignees: TaskPerson[] = [];

  /**
   * List of observers
   */
  @Input() observers: TaskPerson[] = [];

  /**
   * List of participants
   */
  @Input() participants: TaskPerson[] = [];

  /**
   * List of tags
   */
  @Input() tags: TaskTag[] = [];

  /**
   * Task completion percentage (0-100)
   */
  @Input() completionPercentage: number = 0;

  /**
   * Task creation date
   */
  @Input() createdOn: Date = new Date();

  /**
   * Sidebar collapsed state
   */
  @Input() isCollapsed: boolean = false;

  /**
   * View mode flag - disables editing when true
   */
  @Input() isViewMode: boolean = false;

  /**
   * Read-only flag - makes all fields read-only when true
   */
  @Input() isReadOnly: boolean = false;

  /**
   * Array of available statuses from API
   */
  @Input() statuses: any[] = [];

  /**
   * Currently selected status object
   */
  @Input() selectedStatus: any = null;

  /**
   * Modules list (for Module dropdown)
   */
  @Input() modules: any[] = [];

  /**
   * Controls visibility of self-assign/module/reference section
   */
  @Input() showModuleControls: boolean = true;

  /**
   * Lock state - disables all actions when true
   */
  @Input() isLocked: boolean = false;

  // =============================================================================
  // OUTPUTS
  // =============================================================================

  /**
   * Emits when sidebar collapse state changes
   */
  @Output() onCollapse = new EventEmitter<boolean>();

  /**
   * Emits when search is triggered
   */
  @Output() onSearch = new EventEmitter<string>();

  /**
   * Emits when any field value changes
   */
  @Output() fieldChange = new EventEmitter<TaskSidebarFieldChange>();

  /**
   * Emits when status is changed (emits full status object)
   */
  @Output() statusChange = new EventEmitter<any>();

  /**
   * Emits when milestone is selected
   */
  @Output() milestoneSelected = new EventEmitter<TaskMilestone>();

  /**
   * Emits when milestone is removed
   */
  @Output() milestoneRemoved = new EventEmitter<void>();

  /**
   * Emits when assignee selector should open
   */
  @Output() assigneeSelectorOpened = new EventEmitter<void>();

  /**
   * Emits when assignee is removed (with index)
   */
  @Output() assigneeRemoved = new EventEmitter<number>();

  /**
   * Emits when assignee is selected inline (with user object)
   */
  @Output() assigneeSelected = new EventEmitter<any>();

  /**
   * Emits when the user clicks a different row's star in the picker dropdown
   * to make that one the default instead of whichever was positionally first.
   */
  @Output() defaultAssigneeChanged = new EventEmitter<any>();

  /**
   * Emits when observer selector should open
   */
  @Output() observerSelectorOpened = new EventEmitter<void>();

  /**
   * Emits when observer is removed
   */
  @Output() observerRemoved = new EventEmitter<number>();

  /**
   * Emits when observer is selected inline (with user object)
   */
  @Output() observerSelected = new EventEmitter<any>();

  /** Same as defaultAssigneeChanged, for the Observer picker. */
  @Output() defaultObserverChanged = new EventEmitter<any>();

  /**
   * Emits when participant selector should open
   */
  @Output() participantSelectorOpened = new EventEmitter<void>();

  /**
   * Emits when participant is removed
   */
  @Output() participantRemoved = new EventEmitter<number>();

  /**
   * Emits when participant is selected inline (with user object)
   */
  @Output() participantSelected = new EventEmitter<any>();

  /** Same as defaultAssigneeChanged, for the Participant picker. */
  @Output() defaultParticipantChanged = new EventEmitter<any>();

  /**
   * Emits when tag selector should open
   */
  @Output() tagSelectorOpened = new EventEmitter<void>();

  /**
   * Emits when tag is removed
   */
  @Output() tagRemoved = new EventEmitter<number>();

  /**
   * Emits whenever the full set of tags changes in edit mode
   */
  @Output() tagsUpdated = new EventEmitter<TaskTag[]>();

  /**
   * Emits when time planning section is toggled
   */
  @Output() timePlanningToggle = new EventEmitter<boolean>();

  /**
   * Emits when start date changes
   */
  @Output() startDateChange = new EventEmitter<Date>();

  /**
   * Emits when duration changes
   */
  @Output() durationChange = new EventEmitter<number>();

  /**
   * Emits when finish date changes
   */
  @Output() finishDateChange = new EventEmitter<Date>();

  /**
   * Emits when a module is selected (full module object)
   */
  @Output() moduleSelected = new EventEmitter<any>();
  /** Emits when self-assign toggled */
  @Output() selfAssignChange = new EventEmitter<boolean>();

  /**
   * Emits when a reference is selected (id + label)
   */
  @Output() referenceSelected = new EventEmitter<any>();

  // =============================================================================
  // VIEW CHILDREN
  // =============================================================================

  @ViewChild('milestonePopup', { read: ElementRef })
  milestonePopupRef?: ElementRef;

  @ViewChild('tagsNgSelect') tagsNgSelect?: any;

  @ViewChild('assigneeSelect') assigneeSelectRef?: NgSelectComponent;
  @ViewChild('observerSelect') observerSelectRef?: NgSelectComponent;
  @ViewChild('participantSelect') participantSelectRef?: NgSelectComponent;

  // =============================================================================
  // PUBLIC PROPERTIES
  // =============================================================================

  /**
   * Local search query state
   */
  public searchQuery: string = '';

  /**
   * Milestone selector popup visibility
   */
  public showMilestoneSelector: boolean = false;

  /**
   * Toggle button hover state for animation
   */
  public isToggleButtonHovered: boolean = false;

  /**
   * Time Planning section expanded state
   */
  public isTimePlanningExpanded: boolean = false;

  /**
   * Time planning validation error message
   */
  public timePlanningError: string = '';

  /**
   * Inline search visibility flags
   */
  public showAssigneeSearch: boolean = false;
  public showObserverSearch: boolean = false;
  public showParticipantSearch: boolean = false;

  /**
   * User search observables and subjects
   */
  public assigneeSearchInput$ = new Subject<string>();
  public observerSearchInput$ = new Subject<string>();
  public participantSearchInput$ = new Subject<string>();

  // Reference search streams
  public referenceSearchInput$ = new Subject<string>();
  public referenceSearchResults$: Observable<any[]> = of([]);
  public referenceSearchLoading: boolean = false;

  /**
   * Loading states for search
   */
  public assigneeSearchLoading: boolean = false;
  public observerSearchLoading: boolean = false;
  public participantSearchLoading: boolean = false;

  /**
   * Paginated user-picker state. Each list starts as a browsable default page
   * (empty search term) on open and appends further pages via ng-select's
   * (scrollToEnd); typing a search term restarts pagination from page 1.
   */
  private static readonly USERS_PER_PAGE = 12;
  private static readonly SKELETON_ROW_COUNT = 4;

  public assigneeItems: any[] = [];
  public observerItems: any[] = [];
  public participantItems: any[] = [];

  private assigneeSearchTerm = '';
  private observerSearchTerm = '';
  private participantSearchTerm = '';

  private assigneeCurrentPage = 1;
  private observerCurrentPage = 1;
  private participantCurrentPage = 1;

  private assigneeHasMore = true;
  private observerHasMore = true;
  private participantHasMore = true;

  public assigneeLoadingMore = false;
  public observerLoadingMore = false;
  public participantLoadingMore = false;

  /**
   * Tag color mappings
   */
  private readonly TAG_COLORS: { [key: string]: { bg: string; text: string } } =
    {
      blue: { bg: 'rgba(59, 130, 246, 0.2)', text: '#93c5fd' },
      green: { bg: 'rgba(34, 197, 94, 0.2)', text: '#86efac' },
      yellow: { bg: 'rgba(234, 179, 8, 0.2)', text: '#fde047' },
      red: { bg: 'rgba(239, 68, 68, 0.2)', text: '#fca5a5' },
      purple: { bg: 'rgba(168, 85, 247, 0.2)', text: '#d8b4fe' },
      orange: { bg: 'rgba(249, 115, 22, 0.2)', text: '#fdba74' },
      gray: { bg: 'rgba(107, 114, 128, 0.2)', text: '#d1d5db' },
    };

  /**
   * Jira-style tags dropdown state (Edit Mode Only)
   */
  public isTagsDropdownOpen: boolean = false;
  public selectedTagsForDropdown: TaskTag[] = [];
  public tagSearchQuery: string = '';

  /**
   * Get displayed tags - shows tags input (current state)
   */
  public get displayedTags(): TaskTag[] {
    return this.tags || [];
  }

  /**
   * System tags combined from API and current task tags
   */
  public systemTags: TaskTag[] = [];

  /**
   * Get combined tags for dropdown (system tags + task tags merged by name)
   */
  public get combinedTagsForDropdown(): TaskTag[] {
    const taskTags = this.tags || [];
    const apiTags = this.systemTags || [];

    // Create a map to merge by name (case-insensitive)
    const tagMap = new Map<string, TaskTag>();

    // Add system tags first
    apiTags.forEach((tag) => {
      const key = tag.name.toLowerCase();
      if (!tagMap.has(key)) {
        tagMap.set(key, tag);
      }
    });

    // Add task tags (if not already in map from system tags)
    taskTags.forEach((tag) => {
      const key = tag.name.toLowerCase();
      if (!tagMap.has(key)) {
        tagMap.set(key, tag);
      }
    });

    return Array.from(tagMap.values());
  }

  /**
   * Filtered tags based on search query
   */
  public get filteredStaticTags(): TaskTag[] {
    if (!this.tagSearchQuery.trim()) {
      return this.combinedTagsForDropdown;
    }
    const query = this.tagSearchQuery.toLowerCase().trim();
    return this.combinedTagsForDropdown.filter((tag) =>
      tag.name.toLowerCase().includes(query)
    );
  }

  /**
   * Check if a tag is selected
   */
  public isTagSelectedInDropdown(tag: TaskTag): boolean {
    return this.selectedTagsForDropdown.some(
      (selected) => selected.name.toLowerCase() === tag.name.toLowerCase()
    );
  }

  // =============================================================================
  // LIFECYCLE HOOKS
  // =============================================================================

  constructor(
    private backendApiService: BackendApiService,
    private staffService: StaffService,
    private currentUser: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Angular lifecycle hook - component initialization
   */
  ngOnInit(): void {
    this.validateInputs();
    this.setupSearchObservables();
    this.fetchSystemTags();
  }

  /**
   * Fetch system tags from API
   * Fetches all system tags for the task module (not task-specific)
   * @private
   */
  private fetchSystemTags(): void {
    const moduleId = 7; // Task Module ID
    const isSystem = 1;

    this.backendApiService
      .getApi(`user/tags?module_id=${moduleId}&is_system=${isSystem}`)
      .pipe(
        map((response: any) => {
          // Process the API response to match TaskTag interface
          const dataArray = response?.data || response;
          if (Array.isArray(dataArray)) {
            return dataArray.map((tag: any) => ({
              id: tag.id,
              name: tag.tag,
              color: tag.color || 'gray',
            }));
          }
          return [];
        }),
        catchError(() => {
          console.warn('Failed to fetch system tags');
          return of([]);
        })
      )
      .subscribe((tags: TaskTag[]) => {
        this.systemTags = tags;
      });
  }

  /**
   * Set up RxJS observables for user search with debouncing
   * @private
   */
  private setupSearchObservables(): void {
    // Assignee: typing restarts pagination from page 1 (empty term = default browse list)
    this.assigneeSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: string) => {
          this.assigneeSearchTerm = term ?? '';
          this.assigneeSearchLoading = true;
          this.assigneeItems = this.buildSkeletonRows();
          this.cdr.markForCheck();
        }),
        switchMap((term: string) => this.fetchStaffPage(term ?? '', 1, 'assignee'))
      )
      .subscribe((result) => {
        this.assigneeItems = result.items;
        this.assigneeCurrentPage = result.currentPage;
        this.assigneeHasMore = result.hasMore;
        this.assigneeSearchLoading = false;
        this.cdr.markForCheck();
      });

    // Observer: typing restarts pagination from page 1 (empty term = default browse list)
    this.observerSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: string) => {
          this.observerSearchTerm = term ?? '';
          this.observerSearchLoading = true;
          this.observerItems = this.buildSkeletonRows();
          this.cdr.markForCheck();
        }),
        switchMap((term: string) => this.fetchStaffPage(term ?? '', 1, 'observer'))
      )
      .subscribe((result) => {
        this.observerItems = result.items;
        this.observerCurrentPage = result.currentPage;
        this.observerHasMore = result.hasMore;
        this.observerSearchLoading = false;
        this.cdr.markForCheck();
      });

    // Participant: typing restarts pagination from page 1 (empty term = default browse list)
    this.participantSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((term: string) => {
          this.participantSearchTerm = term ?? '';
          this.participantSearchLoading = true;
          this.participantItems = this.buildSkeletonRows();
          this.cdr.markForCheck();
        }),
        switchMap((term: string) => this.fetchStaffPage(term ?? '', 1, 'participant'))
      )
      .subscribe((result) => {
        this.participantItems = result.items;
        this.participantCurrentPage = result.currentPage;
        this.participantHasMore = result.hasMore;
        this.participantSearchLoading = false;
        this.cdr.markForCheck();
      });

    // Reference search observable
    this.referenceSearchResults$ = this.referenceSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        const moduleId = this.taskForm?.get('module_id')?.value;
        if (!moduleId || !searchTerm || searchTerm.trim().length < 2) {
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        const selectedModule = this.modules?.find(
          (m: any) => Number(m.id) === Number(moduleId)
        );
        if (!selectedModule) {
          this.referenceSearchLoading = false;
          this.cdr.markForCheck();
          return of([]);
        }

        const payload = {
          type: selectedModule.slug,
          module_id: selectedModule.id,
          search: searchTerm.trim(),
        };

        this.referenceSearchLoading = true;
        this.cdr.markForCheck();
        return this.backendApiService
          .postApi('tasks/search-reference-numbers', payload)
          .pipe(
            map((response: any) => {
              this.referenceSearchLoading = false;
              this.cdr.markForCheck();
              const list = response?.data?.data || [];
              return list.map((x: any) => ({ id: x.id, name: x.data }));
            }),
            catchError(() => {
              this.referenceSearchLoading = false;
              this.cdr.markForCheck();
              return of([]);
            })
          );
      })
    );
  }

  /**
   * Fetches one page of the staff picker (empty `search` returns the default
   * browsable list). Shared by the assignee/observer/participant pickers for
   * both the initial/typed-search load and scroll-triggered pagination.
   */
  private fetchStaffPage(
    search: string,
    page: number,
    personType?: 'assignee' | 'observer' | 'participant'
  ): Observable<{ items: any[]; currentPage: number; hasMore: boolean }> {
    return this.backendApiService
      .getApi('staff/search', { search, page, per_page: TaskSidebarComponent.USERS_PER_PAGE })
      .pipe(
        map((response: any) => {
          const items = this.staffService.processStaff(response.data);
          const currentPage = response.meta?.current_page ?? page;
          const hasMore = currentPage < (response.meta?.last_page ?? currentPage);
          return { items, currentPage, hasMore };
        }),
        catchError(() => of({ items: [], currentPage: page, hasMore: false }))
      );
  }

  /**
   * Whether the star should show next to an already-selected Assignee/
   * Observer/Participant. Three cases: `isDefault` (camelCase) is set
   * transiently, in memory, the moment a person is picked/promoted during
   * THIS session; `is_default` (snake_case) is the real, persisted flag on
   * that task_associations row, returned by the API for an already-saved
   * task; and for a brand-new task, savedDefaults (from GET
   * tasks/default-people) is used as a fallback while pre-filling empty
   * fields. `isDefault === false` is an explicit local override (the user
   * just promoted someone ELSE in this same list) — it must win over the
   * savedDefaults fallback, otherwise the old server-saved default keeps
   * matching by id and both people show starred at once.
   */
  isPersonDefault(person: any, personType: 'assignee' | 'observer' | 'participant'): boolean {
    if (!person) {
      return false;
    }
    if (person.isDefault === true || person.is_default === true) {
      return true;
    }
    if (person.isDefault === false) {
      return false;
    }
    const savedId = this.savedDefaults?.[personType]?.id;
    return savedId != null && String(savedId) === String(person.id);
  }

  /**
   * Placeholder rows rendered in the picker while a page is in flight
   * (initial open, typed search, or scroll-triggered pagination).
   */
  private buildSkeletonRows(): any[] {
    return Array.from({ length: TaskSidebarComponent.SKELETON_ROW_COUNT }, (_, i) => ({
      __skeleton: true,
      id: `skeleton-${i}`,
    }));
  }

  /**
   * Angular lifecycle hook - detect input changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskForm']) {
      this.validateInputs();
    }
    // Always sync selectedTagsForDropdown with tags input for all modes
    if (changes['tags']) {
      this.selectedTagsForDropdown = [...(this.tags || [])];
    }
    // Initialize selectedTagsForDropdown when taskId changes
    if (changes['taskId'] && this.tags) {
      this.selectedTagsForDropdown = [...(this.tags || [])];
    }
  }

  /**
   * Close milestone popup when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showMilestoneSelector && this.milestonePopupRef) {
      const clickedInside = this.milestonePopupRef.nativeElement.contains(
        event.target
      );
      const clickedButton = (event.target as HTMLElement).closest(
        '.milestone-add-btn'
      );

      // Only close if click is OUTSIDE popup AND not on the trigger button
      if (!clickedInside && !clickedButton) {
        this.showMilestoneSelector = false;
      }
    }

    // Close tags dropdown when clicking outside (Edit Mode Only)
    if (
      this.isTagsDropdownOpen &&
      this.isEditingExistingTask() &&
      !this.isViewMode
    ) {
      const clickedElement = event.target as HTMLElement;
      const tagsContainer = clickedElement.closest('.tags-container');
      const tagsDropdown = clickedElement.closest('.tags-dropdown-wrapper');
      const tagsNgSelectWrapper = clickedElement.closest(
        '.tags-ng-select-wrapper'
      );
      const ngSelectPanel = clickedElement.closest('.ng-dropdown-panel');
      const ngSelectContainer = clickedElement.closest('.ng-select-container');

      // Close if click is outside all tag-related elements
      if (
        !tagsContainer &&
        !tagsDropdown &&
        !tagsNgSelectWrapper &&
        !ngSelectPanel &&
        !ngSelectContainer
      ) {
        this.closeTagsDropdown();
      }
    }
  }

  // =============================================================================
  // VALIDATION
  // =============================================================================

  /**
   * Validate required inputs are provided
   * @private
   */
  private validateInputs(): void {
    if (!this.taskForm) {
      console.warn('TaskSidebarComponent: taskForm input is required');
    }
  }

  // =============================================================================
  // SIDEBAR ACTIONS
  // =============================================================================

  /**
   * Toggle sidebar collapse state
   */
  public toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    this.onCollapse.emit(this.isCollapsed);
  }

  /**
   * Expand sidebar (from collapsed state)
   */
  public expandSidebar(): void {
    this.isCollapsed = false;
    this.onCollapse.emit(false);
  }

  /**
   * Collapse sidebar (to collapsed state)
   */
  public collapseSidebar(): void {
    this.isCollapsed = true;
    this.onCollapse.emit(true);
  }

  /**
   * Get animation state for sidebar collapse trigger
   */
  public getSidebarAnimationState(): string {
    return this.isCollapsed ? 'collapsed' : 'expanded';
  }

  /**
   * Get animation state for toggle button hover
   */
  public getToggleButtonAnimationState(): string {
    return this.isToggleButtonHovered ? 'hovered' : 'normal';
  }

  /**
   * Handle search input changes
   * @param query Search query string
   */
  public handleSearch(query: string): void {
    this.searchQuery = query;
    this.onSearch.emit(query);
  }

  /**
   * Handle field value changes and emit to parent
   * @param field Field name
   * @param event Change event
   */
  public handleFieldChange(field: string, event: any): void {
    const value = event?.target?.value ?? event;
    const change: TaskSidebarFieldChange = {
      field,
      value,
    };
    this.fieldChange.emit(change);
  }

  /**
   * Handle status change from mat-select dropdown
   * Emits full status object to parent
   * @param event MatSelectChange event
   */
  public handleStatusChange(event: any): void {
    const statusId = event.value;
    const selectedStatusObj = this.statuses.find((s) => s.id === statusId);

    if (selectedStatusObj) {
      this.statusChange.emit(selectedStatusObj);
    }
  }

  /**
   * Handle module selection change from dropdown
   * - Emits fieldChange for form control update
   * - Emits moduleSelected with full module object for parent workflows
   */
  public handleModuleChange(event: any): void {
    const selectedId = event?.target?.value ?? event;

    // Emit form control change for module_id
    this.fieldChange.emit({ field: 'module_id', value: Number(selectedId) });

    // Find and emit full module object
    const selectedModule = this.modules?.find(
      (m: any) => String(m.id) === String(selectedId)
    );
    if (selectedModule) {
      this.moduleSelected.emit(selectedModule);
    }

    // Clear reference when module changes
    this.fieldChange.emit({ field: 'reference_id', value: null });
  }

  /**
   * Handle reference selection from typeahead dropdown
   */
  public onReferenceSelect(ref: any): void {
    if (!ref) {
      return;
    }
    this.fieldChange.emit({ field: 'reference_id', value: ref.id });
    this.referenceSelected.emit(ref);
  }

  /** Handle self-assign toggle change */
  public onSelfAssignToggle(event: any): void {
    const isOn = !!(event && (event.checked ?? event.target?.checked));
    this.fieldChange.emit({ field: 'self_assign', value: isOn ? 1 : 0 });
    this.selfAssignChange.emit(isOn);

    if (isOn) {
      const loginUserId = this.currentUser?.getID?.() ?? null;
      this.fieldChange.emit({ field: 'module_id', value: 11 });
      if (loginUserId) {
        this.fieldChange.emit({
          field: 'reference_id',
          value: Number(loginUserId),
        });
      }
      this.referenceSearchInput$.next('');
    } else {
      this.fieldChange.emit({ field: 'module_id', value: null });
      this.fieldChange.emit({ field: 'reference_id', value: null });
    }
  }

  /**
   * Get selected status name for display in trigger
   * @returns Status name or empty string if not found
   */
  public getSelectedStatusName(): string {
    const statusId = this.taskForm?.get('status')?.value;
    if (!statusId || !this.statuses) return '';

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.name || '';
  }

  /**
   * Get selected status color for display in trigger
   * @returns Status color (hex code) or default gray if not found
   */
  public getSelectedStatusColor(): string {
    const statusId = this.taskForm?.get('status')?.value;
    if (!statusId || !this.statuses) return '#9ca3af'; // Default gray

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.color || '#9ca3af';
  }

  /**
   * Get selected status lock state for display in trigger
   * @returns True if selected status has lock_status enabled
   */
  public getSelectedStatusLocked(): boolean {
    const statusId = this.taskForm?.get('status')?.value;
    if (!statusId || !this.statuses) return false;

    const status = this.statuses.find((s) => s.id === statusId);
    return status?.lock_status || false;
  }

  // =============================================================================
  // MILESTONE METHODS
  // =============================================================================

  /**
   * Toggle milestone selector popup visibility
   */
  public toggleMilestoneSelector(): void {
    this.showMilestoneSelector = !this.showMilestoneSelector;
  }

  /**
   * Select a milestone
   * @param milestone Selected milestone object
   */
  public selectMilestone(milestone: TaskMilestone): void {
    this.showMilestoneSelector = false;
    this.milestoneSelected.emit(milestone);
  }

  /**
   * Remove selected milestone
   */
  public removeMilestone(): void {
    this.milestoneRemoved.emit();
  }

  // =============================================================================
  // PEOPLE METHODS
  // =============================================================================

  /**
   * Toggle assignee inline search
   */
  public openAssigneeSelector(): void {
    this.showAssigneeSearch = !this.showAssigneeSearch;
    if (this.showAssigneeSearch) {
      this.loadAssigneeBrowseList();
      // ng-select isn't in the DOM until this change detection pass finishes,
      // so ViewChild only resolves on the next tick.
      setTimeout(() => this.assigneeSelectRef?.open());
    } else {
      // Clear search when closing
      this.assigneeSearchInput$.next('');
    }
  }

  /**
   * Populate the assignee picker with the default browsable list (page 1,
   * empty search term) as soon as it opens, instead of leaving it empty
   * until the user types.
   */
  private loadAssigneeBrowseList(): void {
    this.assigneeSearchTerm = '';
    this.assigneeSearchLoading = true;
    this.assigneeItems = this.buildSkeletonRows();
    this.fetchStaffPage('', 1, 'assignee').subscribe((result) => {
      this.assigneeItems = result.items;
      this.assigneeCurrentPage = result.currentPage;
      this.assigneeHasMore = result.hasMore;
      this.assigneeSearchLoading = false;
      this.cdr.markForCheck();
    });
  }

  /**
   * Fetch the next page when the assignee dropdown is scrolled to the end.
   */
  public onAssigneeScrollToEnd(): void {
    if (this.assigneeSearchLoading || this.assigneeLoadingMore || !this.assigneeHasMore) {
      return;
    }
    this.assigneeLoadingMore = true;
    const loadedItems = this.assigneeItems;
    this.assigneeItems = [...loadedItems, ...this.buildSkeletonRows()];
    this.cdr.markForCheck();
    this.fetchStaffPage(this.assigneeSearchTerm, this.assigneeCurrentPage + 1, 'assignee').subscribe(
      (result) => {
        this.assigneeItems = [...loadedItems, ...result.items];
        this.assigneeCurrentPage = result.currentPage;
        this.assigneeHasMore = result.hasMore;
        this.assigneeLoadingMore = false;
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Handle assignee selection from ng-select
   * @param user Selected user object
   */
  public onAssigneeSelect(user: any): void {
    if (user && !user.__skeleton) {
      this.assigneeSelected.emit(user);
      // Hide search and clear after selection
      this.showAssigneeSearch = false;
      this.assigneeSearchInput$.next('');
    }
  }

  /**
   * Star next to an ALREADY-selected assignee (in the People list itself,
   * not the dropdown) — promotes them to default. Reuses the same
   * defaultAssigneeChanged event the dropdown's star uses; the parent
   * handler knows how to remap the flag in place since this person is
   * already in the list.
   */
  public setSelectedAssigneeAsDefault(person: any, event: Event): void {
    event.stopPropagation();
    this.defaultAssigneeChanged.emit({ ...person, isDefault: true });
  }

  /**
   * Remove assignee by index
   * @param index Assignee index in array
   */
  public removeAssignee(index: number): void {
    this.assigneeRemoved.emit(index);
  }

  /**
   * Toggle observer inline search
   */
  public openObserverSelector(): void {
    this.showObserverSearch = !this.showObserverSearch;
    if (this.showObserverSearch) {
      this.loadObserverBrowseList();
      // ng-select isn't in the DOM until this change detection pass finishes,
      // so ViewChild only resolves on the next tick.
      setTimeout(() => this.observerSelectRef?.open());
    } else {
      // Clear search when closing
      this.observerSearchInput$.next('');
    }
  }

  /**
   * Populate the observer picker with the default browsable list (page 1,
   * empty search term) as soon as it opens, instead of leaving it empty
   * until the user types.
   */
  private loadObserverBrowseList(): void {
    this.observerSearchTerm = '';
    this.observerSearchLoading = true;
    this.observerItems = this.buildSkeletonRows();
    this.fetchStaffPage('', 1, 'observer').subscribe((result) => {
      this.observerItems = result.items;
      this.observerCurrentPage = result.currentPage;
      this.observerHasMore = result.hasMore;
      this.observerSearchLoading = false;
      this.cdr.markForCheck();
    });
  }

  /**
   * Fetch the next page when the observer dropdown is scrolled to the end.
   */
  public onObserverScrollToEnd(): void {
    if (this.observerSearchLoading || this.observerLoadingMore || !this.observerHasMore) {
      return;
    }
    this.observerLoadingMore = true;
    const loadedItems = this.observerItems;
    this.observerItems = [...loadedItems, ...this.buildSkeletonRows()];
    this.cdr.markForCheck();
    this.fetchStaffPage(this.observerSearchTerm, this.observerCurrentPage + 1, 'observer').subscribe(
      (result) => {
        this.observerItems = [...loadedItems, ...result.items];
        this.observerCurrentPage = result.currentPage;
        this.observerHasMore = result.hasMore;
        this.observerLoadingMore = false;
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Handle observer selection from ng-select
   * @param user Selected user object
   */
  public onObserverSelect(user: any): void {
    if (user && !user.__skeleton) {
      this.observerSelected.emit(user);
      // Hide search and clear after selection
      this.showObserverSearch = false;
      this.observerSearchInput$.next('');
    }
  }

  /** Same as setSelectedAssigneeAsDefault, for the Observer list. */
  public setSelectedObserverAsDefault(person: any, event: Event): void {
    event.stopPropagation();
    this.defaultObserverChanged.emit({ ...person, isDefault: true });
  }

  /**
   * Remove observer by index
   * @param index Observer index in array
   */
  public removeObserver(index: number): void {
    this.observerRemoved.emit(index);
  }

  /**
   * Toggle participant inline search
   */
  public openParticipantSelector(): void {
    this.showParticipantSearch = !this.showParticipantSearch;
    if (this.showParticipantSearch) {
      this.loadParticipantBrowseList();
      // ng-select isn't in the DOM until this change detection pass finishes,
      // so ViewChild only resolves on the next tick.
      setTimeout(() => this.participantSelectRef?.open());
    } else {
      // Clear search when closing
      this.participantSearchInput$.next('');
    }
  }

  /**
   * Populate the participant picker with the default browsable list (page 1,
   * empty search term) as soon as it opens, instead of leaving it empty
   * until the user types.
   */
  private loadParticipantBrowseList(): void {
    this.participantSearchTerm = '';
    this.participantSearchLoading = true;
    this.participantItems = this.buildSkeletonRows();
    this.fetchStaffPage('', 1, 'participant').subscribe((result) => {
      this.participantItems = result.items;
      this.participantCurrentPage = result.currentPage;
      this.participantHasMore = result.hasMore;
      this.participantSearchLoading = false;
      this.cdr.markForCheck();
    });
  }

  /**
   * Fetch the next page when the participant dropdown is scrolled to the end.
   */
  public onParticipantScrollToEnd(): void {
    if (this.participantSearchLoading || this.participantLoadingMore || !this.participantHasMore) {
      return;
    }
    this.participantLoadingMore = true;
    const loadedItems = this.participantItems;
    this.participantItems = [...loadedItems, ...this.buildSkeletonRows()];
    this.cdr.markForCheck();
    this.fetchStaffPage(this.participantSearchTerm, this.participantCurrentPage + 1, 'participant').subscribe(
      (result) => {
        this.participantItems = [...loadedItems, ...result.items];
        this.participantCurrentPage = result.currentPage;
        this.participantHasMore = result.hasMore;
        this.participantLoadingMore = false;
        this.cdr.markForCheck();
      }
    );
  }

  /**
   * Handle participant selection from ng-select
   * @param user Selected user object
   */
  public onParticipantSelect(user: any): void {
    if (user && !user.__skeleton) {
      this.participantSelected.emit(user);
      // Hide search and clear after selection
      this.showParticipantSearch = false;
      this.participantSearchInput$.next('');
    }
  }

  /** Same as setSelectedAssigneeAsDefault, for the Participant list. */
  public setSelectedParticipantAsDefault(person: any, event: Event): void {
    event.stopPropagation();
    this.defaultParticipantChanged.emit({ ...person, isDefault: true });
  }

  /**
   * Remove participant by index
   * @param index Participant index in array
   */
  public removeParticipant(index: number): void {
    this.participantRemoved.emit(index);
  }

  // =============================================================================
  // TAG METHODS
  // =============================================================================

  /**
   * Open tag selector popup for creating new tags
   * Emits event to parent component to handle popup
   */
  public openTagSelector(): void {
    this.tagSelectorOpened.emit();
  }

  /**
   * Remove tag by clicking X button on chip
   * Works in all modes - triggers immediate sync to parent
   */
  public removeTagFromDisplayed(index: number): void {
    const currentTags = this.tags || [];
    if (index < 0 || index >= currentTags.length) return;

    const updatedTags = [
      ...currentTags.slice(0, index),
      ...currentTags.slice(index + 1),
    ];

    // Update both tags input and dropdown selection
    this.selectedTagsForDropdown = [...updatedTags];

    // Sync to parent immediately
    this.tagsUpdated.emit(updatedTags);
  }

  /**
   * Open tags dropdown - Works for all modes (Create, Edit, View)
   */
  public openTagsDropdown(): void {
    // Sync selected tags from input tags
    // Map current tags to match combined tags structure
    this.selectedTagsForDropdown = (this.tags || []).map((tag) => {
      // Find matching tag in combined list or use the tag as-is
      const matchingTag = this.combinedTagsForDropdown.find(
        (ct) => ct.name.toLowerCase() === tag.name.toLowerCase()
      );
      return matchingTag || { ...tag };
    });
    this.tagSearchQuery = '';
    this.isTagsDropdownOpen = true;

    // Focus ng-select after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (this.tagsNgSelect) {
        this.tagsNgSelect.focus();
      }
    }, 100);
  }

  /**
   * Close tags dropdown - Works for all modes
   */
  public closeTagsDropdown(): void {
    // Close ng-select if it's open
    if (this.tagsNgSelect && this.tagsNgSelect.isOpen) {
      this.tagsNgSelect.close();
    }
    this.isTagsDropdownOpen = false;
    this.tagSearchQuery = '';
    // Sync selected tags back to parent
    this.syncTagsToParent();
  }

  /**
   * Toggle tag selection in dropdown - Works for all modes
   * @param tag Tag to toggle
   */
  public toggleTagSelection(tag: TaskTag): void {
    const index = this.selectedTagsForDropdown.findIndex(
      (selected) => selected.name.toLowerCase() === tag.name.toLowerCase()
    );

    if (index > -1) {
      // Remove tag
      this.selectedTagsForDropdown = [
        ...this.selectedTagsForDropdown.slice(0, index),
        ...this.selectedTagsForDropdown.slice(index + 1),
      ];
    } else {
      // Add tag
      this.selectedTagsForDropdown = [
        ...this.selectedTagsForDropdown,
        { ...tag },
      ];
    }

    // Sync immediately for all modes
    this.syncTagsToParent();
  }

  /**
   * Handle tag selection from ng-select - Works for all modes
   * @param selectedTags Array of selected tags from ng-select
   */
  public onTagsSelectChange(selectedTags: TaskTag[]): void {
    this.selectedTagsForDropdown = selectedTags || [];
    this.syncTagsToParent();
  }

  /**
   * Sync selected tags to parent component - Works for all modes
   * In View mode, parent will make immediate API calls
   * In Create/Edit mode, parent will update form control
   */
  private syncTagsToParent(): void {
    const normalizedTags: TaskTag[] = (this.selectedTagsForDropdown || []).map(
      (tag) => ({
        id: tag.id,
        name: tag.name,
        color: tag.color || 'gray',
      })
    );

    this.tagsUpdated.emit(normalizedTags);
  }

  /**
   * Handle ESC key to close dropdown
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (
      this.isTagsDropdownOpen &&
      this.isEditingExistingTask() &&
      !this.isViewMode
    ) {
      this.closeTagsDropdown();
    }
  }

  /**
   * Handle chip click to open dropdown (Edit Mode Only)
   * @param event Click event
   */
  public onTagChipClick(event: Event): void {
    if (this.isEditingExistingTask() && !this.isViewMode) {
      event.stopPropagation();
      this.openTagsDropdown();
    }
  }

  /**
   * Compare function for ng-select to match tags by name
   * Arrow function to preserve 'this' context in template
   */
  public compareTags = (tag1: TaskTag, tag2: TaskTag): boolean => {
    if (!tag1 || !tag2) return false;
    return tag1.name?.toLowerCase() === tag2.name?.toLowerCase();
  };

  /**
   * Track by function for ng-select to properly identify items
   */
  public trackByTagName = (index: number, item: TaskTag): string => {
    return item?.name?.toLowerCase() || index.toString();
  };

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Get tag background color
   * @param color Color key
   * @returns RGBA color string
   */
  public getTagBackgroundColor(color: string): string {
    if (!color) {
      return this.TAG_COLORS.gray.bg;
    }

    // If color is a HEX (e.g. #22c55e), use a translucent background from it
    if (this.isHexColor(color)) {
      return this.hexToRgba(color, 0.2);
    }

    return this.TAG_COLORS[color]?.bg || this.TAG_COLORS.gray.bg;
  }

  /**
   * Get tag text color
   * @param color Color key
   * @returns Hex color string
   */
  public getTagTextColor(color: string): string {
    if (!color) {
      return this.TAG_COLORS.gray.text;
    }

    // If color is a HEX, use it directly for the text color
    if (this.isHexColor(color)) {
      return color;
    }

    return this.TAG_COLORS[color]?.text || this.TAG_COLORS.gray.text;
  }

  /** Determine if a string is a HEX color like #fff or #ffffff */
  private isHexColor(value: string): boolean {
    return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test((value || '').trim());
  }

  /** Convert HEX color to rgba string with provided alpha */
  private hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const expand =
      clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean;
    const r = parseInt(expand.substring(0, 2), 16);
    const g = parseInt(expand.substring(2, 4), 16);
    const b = parseInt(expand.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Get form control value by field name
   * @param fieldName Form control name
   * @returns Control value or null
   */
  public getControlValue(fieldName: string): any {
    return this.taskForm?.get(fieldName)?.value ?? null;
  }

  /**
   * Check if we're editing an existing task
   * @returns true if taskId is a number (editing), false if 'New' or empty (creating)
   */
  public isEditingExistingTask(): boolean {
    return (
      typeof this.taskId === 'number' ||
      (typeof this.taskId === 'string' &&
        this.taskId !== 'New' &&
        !isNaN(Number(this.taskId)))
    );
  }

  /**
   * Check if form control has errors
   * @param fieldName Form control name
   * @returns True if control has errors
   */
  public hasError(fieldName: string): boolean {
    const control = this.taskForm?.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get human-readable status text for milestone
   * @param status Milestone status
   * @returns Formatted status text
   */
  public getMilestoneStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      upcoming: 'Upcoming',
      'in-progress': 'In Progress',
      completed: 'Completed',
      overdue: 'Overdue',
    };
    return statusMap[status] || status;
  }

  /**
   * Calculate days remaining until milestone due date
   * @param milestone Milestone object
   * @returns Number of days remaining (can be negative if overdue)
   */
  public calculateDaysRemaining(milestone: TaskMilestone): number {
    if (!milestone.dueDate) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = new Date(milestone.dueDate);
    due.setHours(0, 0, 0, 0);

    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  // =============================================================================
  // TIME PLANNING METHODS
  // =============================================================================

  /**
   * Toggle time planning section expanded state
   */
  public toggleTimePlanning(): void {
    this.isTimePlanningExpanded = !this.isTimePlanningExpanded;
    this.timePlanningToggle.emit(this.isTimePlanningExpanded);
  }

  /**
   * Handle start date change event
   * @param event Change event from date input
   */
  public onStartDateChange(event: any): void {
    const startDate = event?.target?.value;
    if (startDate) {
      this.startDateChange.emit(new Date(startDate));
      this.validateTimePlanning();
      this.calculateDurationFromDates();
    }
  }

  /**
   * Handle duration change event
   * @param event Input event from duration field
   */
  public onDurationChange(event: any): void {
    const duration = parseFloat(event.target.value);
    if (!isNaN(duration) && duration >= 0) {
      this.durationChange.emit(duration);
      this.taskForm?.get('duration')?.setValue(duration, { emitEvent: false });
      this.calculateFinishDate();
    }
  }

  /**
   * Handle finish date change event
   * @param event Change event from date input
   */
  public onFinishDateChange(event: any): void {
    const finishDate = event?.target?.value;
    if (finishDate) {
      this.finishDateChange.emit(new Date(finishDate));
      this.validateTimePlanning();
      this.calculateDurationFromDates();
    }
  }

  /**
   * Validate time planning dates (start must be before finish)
   * @private
   */
  private validateTimePlanning(): void {
    const startDateValue = this.taskForm?.get('start_date')?.value;
    const finishDateValue = this.taskForm?.get('date_finished')?.value;

    if (startDateValue && finishDateValue) {
      const start = new Date(startDateValue);
      const finish = new Date(finishDateValue);

      // Reset hours to compare dates only
      start.setHours(0, 0, 0, 0);
      finish.setHours(0, 0, 0, 0);

      if (start > finish) {
        this.timePlanningError = 'Start date cannot be after finish date';
      } else {
        this.timePlanningError = '';
      }
    } else {
      this.timePlanningError = '';
    }
  }

  /**
   * Calculate finish date based on start date and duration
   * @private
   */
  private calculateFinishDate(): void {
    const startDateValue = this.taskForm?.get('start_date')?.value;
    const duration = this.taskForm?.get('duration')?.value;

    if (startDateValue && duration && duration > 0) {
      const start = new Date(startDateValue);
      const finish = new Date(start);
      finish.setDate(finish.getDate() + duration);

      // Format as YYYY-MM-DD for date input
      const finishFormatted = finish.toISOString().split('T')[0];
      this.taskForm
        ?.get('date_finished')
        ?.setValue(finishFormatted, { emitEvent: false });
      this.validateTimePlanning();
    }
  }

  /**
   * Calculate duration based on start and finish dates
   * @private
   */
  private calculateDurationFromDates(): void {
    const startDateValue = this.taskForm?.get('start_date')?.value;
    const finishDateValue = this.taskForm?.get('date_finished')?.value;

    if (startDateValue && finishDateValue) {
      const start = new Date(startDateValue);
      const finish = new Date(finishDateValue);

      // Reset hours to compare dates only
      start.setHours(0, 0, 0, 0);
      finish.setHours(0, 0, 0, 0);

      if (start <= finish) {
        const diffTime = finish.getTime() - start.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        this.taskForm
          ?.get('duration')
          ?.setValue(diffDays, { emitEvent: false });
      }
    }
  }
}
